/**
 * Topic landing pages — registry mapping the home-page dimensions to wiki
 * topic chapters across countries.
 *
 * Each entry binds:
 *   - the home-page dimension card (which appears in DimensionsGrid),
 *   - the wiki topic slug (matches PUBLISHED_TOPICS),
 *   - a country-data path (so the landing page can rank all 30 countries
 *     by their measured score on this dimension),
 *   - SEO + display metadata.
 *
 * URL pattern: /topics/<slug>  (EN canonical), /ru/topics/<slug> (RU).
 * Slug stays kebab-case ASCII and is the same in every language.
 *
 * When a new dimension or topic ships, add it here and to the route table
 * in App.tsx.  No other wiring is needed; sitemap + landing page pick it
 * up automatically.
 */

import type { LangCode, TopicSlug } from './architecture';

export interface TopicLandingDef {
  /** URL slug — matches the home DimensionsGrid card key. */
  slug: string;
  /** The wiki topic this landing routes into per country. */
  wikiTopic: TopicSlug;
  /** Dimension token used for colour and quiz score lookup. `null` means
   *  the dimension is unique to this landing (e.g. property tax — no quiz
   *  score, ranks by KB tier instead). */
  quizDimension:
    | 'visa'
    | 'tax'
    | 'cost'
    | 'climate'
    | 'healthcare'
    | 'safety'
    | 'language'
    | 'jobs'
    | 'family'
    | 'banking'
    | null;
  /** Dotted path inside countries.json metrics for the per-country score (0-10).
   *  When `null`, the page skips the ranked list. */
  metricPath: string | null;
  /** Emoji thumbnail for the hero. */
  icon: string;
  /** Optional source attribution shown in the hero metadata strip. */
  source: string;
}

export const TOPIC_LANDINGS: readonly TopicLandingDef[] = [
  { slug: 'visa',          wikiTopic: 'visa',             quizDimension: 'visa',       metricPath: 'metrics.visa.score',           icon: '🛂', source: 'Gov. portals · 2024' },
  { slug: 'tax',           wikiTopic: 'taxes',            quizDimension: 'tax',        metricPath: 'metrics.tax.score',            icon: '🧮', source: 'OECD · 2024' },
  { slug: 'property-tax',  wikiTopic: 'property',         quizDimension: null,         metricPath: null,                           icon: '🏠', source: 'Lodestar KB · 2026-Q2' },
  { slug: 'cost',          wikiTopic: 'cost-of-living',   quizDimension: 'cost',       metricPath: 'metrics.cost.score',           icon: '💶', source: 'Numbeo · 2024' },
  { slug: 'climate',       wikiTopic: 'climate',          quizDimension: 'climate',    metricPath: 'metrics.climate.score',        icon: '🌤', source: 'Open-Meteo · 1991-2020' },
  { slug: 'healthcare',    wikiTopic: 'healthcare',       quizDimension: 'healthcare', metricPath: 'metrics.healthcare.score',     icon: '🏥', source: 'WHO UHC · 2023' },
  { slug: 'safety',        wikiTopic: 'safety-community', quizDimension: 'safety',     metricPath: 'metrics.safety.score',          icon: '🛡', source: 'IEP GPI · 2024' },
  { slug: 'language',      wikiTopic: 'language-culture', quizDimension: 'language',   metricPath: 'metrics.language.score',        icon: '💬', source: 'EF EPI · 2025' },
  { slug: 'jobs',          wikiTopic: 'infrastructure',   quizDimension: 'jobs',       metricPath: 'metrics.jobs.score',            icon: '📡', source: 'Ookla · 2024' },
  { slug: 'family',        wikiTopic: 'education',        quizDimension: 'family',     metricPath: 'metrics.family.score',          icon: '🎓', source: 'UNESCO + IB · 2024' },
  { slug: 'banking',       wikiTopic: 'banking',          quizDimension: 'banking',    metricPath: 'metrics.banking.score',         icon: '🏦', source: 'Wise · FATF · 2024' },
  { slug: 'transport',     wikiTopic: 'transport',        quizDimension: null,         metricPath: null,                           icon: '🚆', source: 'OpenStreetMap + national rail' },
] as const;

export type TopicLandingSlug = (typeof TOPIC_LANDINGS)[number]['slug'];

export const TOPIC_LANDING_SLUGS: readonly TopicLandingSlug[] = TOPIC_LANDINGS.map(
  (t) => t.slug,
);

export function getTopicLanding(slug: string): TopicLandingDef | null {
  return TOPIC_LANDINGS.find((t) => t.slug === slug) ?? null;
}

export function isTopicLandingSlug(s: unknown): s is TopicLandingSlug {
  return typeof s === 'string' && TOPIC_LANDING_SLUGS.includes(s as TopicLandingSlug);
}

/**
 * URL builder.  EN canonical has no language prefix; non-default langs use
 * `/<lang>/` prefix the same way wiki URLs do (architecture.ts).
 */
export function buildTopicLandingUrl(opts: {
  slug: TopicLandingSlug;
  lang: LangCode;
  origin?: string;
}): string {
  const { slug, lang, origin } = opts;
  const prefix = lang === 'en' ? '' : `/${lang}`;
  const path = `${prefix}/topics/${slug}`;
  return origin ? `${origin}${path}` : path;
}
